import type { ConnectionState } from 'phoenix';
import { Socket as PhoenixSocket } from 'phoenix';

import { WebSocketConnectionState } from 'common/constants/webSocketConnectionState';
import { webSocketStateSubject } from 'common/helpers/webSocketState';

export class PhoenixSocketTokenHolder {
  private token: string | null = null;
  private promise: Promise<string> | null = null;
  private tokenUsed = true;

  constructor(private fetchTokenFn: () => Promise<string>) {}

  async fetchNewTokenIfNeeded() {
    if (this.promise || !this.tokenUsed) {
      return;
    }

    this.promise = this.fetchTokenFn();
    try {
      this.token = await this.promise;
      this.tokenUsed = false;
    } finally {
      this.promise = null;
    }
  }

  getToken() {
    this.tokenUsed = true;
    return this.token;
  }
}

let phnxSocket: PhoenixSocket | null = null;
const HEARTBEAT_INTERVAL_MS = 15_000;
export type SetupPhoenixSocketProps = {
  fetchWsToken: () => Promise<string>;
  getWsUrl: () => string;
};

export async function setupPhoenixSocket({ fetchWsToken, getWsUrl }: SetupPhoenixSocketProps) {
  const phoenixSocketTokenHolder = new PhoenixSocketTokenHolder(fetchWsToken);
  await phoenixSocketTokenHolder.fetchNewTokenIfNeeded();

  phnxSocket = new PhoenixSocket(getWsUrl(), {
    heartbeatIntervalMs: HEARTBEAT_INTERVAL_MS,
    params: () => {
      return { token: phoenixSocketTokenHolder.getToken() };
    },
  });
  // PhoenixSocket calls params function automatically before attempting to establish a connection.
  // Since it cannot be an async function we have to resort to nasty hacks.
  // All hail JavaScript for making this possible.
  phnxSocket.onError(() => {
    phoenixSocketTokenHolder.fetchNewTokenIfNeeded();
  });

  phnxSocket.onOpen(() => {
    webSocketStateSubject.next(WebSocketConnectionState.OPEN);
  });

  phnxSocket.onClose(() => {
    webSocketStateSubject.next(WebSocketConnectionState.CLOSED);
  });

  // When we go offline, heartbeat msg is sent and stored in WebSocket buffer. After HEARTBEAT_INTERVAL_MS time, if
  // heartbeat response is not returned, PhoenixSocket will try to close the socket. WebSocket with (heartbeat) msg in
  // buffer cannot be closed, so it will be in 'closing' state. Since PhoenixSocket doesn't support `onClosing`
  // callback, we relay on the fact that if we don't receive any message after 2 * HEARTBEAT_INTERVAL_MS then
  // socket changed its state and that value should be sent to observable (probably 'closing' state).
  let heartbeatTimer: number;
  phnxSocket.onMessage(() => {
    if (heartbeatTimer) window.clearTimeout(heartbeatTimer);

    heartbeatTimer = window.setTimeout(() => {
      webSocketStateSubject.next(connectionStateToWebSocketState(phnxSocket!.connectionState()));
    }, 2 * HEARTBEAT_INTERVAL_MS);
  });

  return phnxSocket;
}

export const closeWsConnection = () => {
  if (phnxSocket) {
    phnxSocket.disconnect();
  }
};

export const openWsConnectionIfNotConnected = () => {
  if (!phnxSocket?.isConnected()) {
    phnxSocket?.connect();
  }
};

function connectionStateToWebSocketState(connectionState: ConnectionState) {
  switch (connectionState) {
    case 'connecting':
      return WebSocketConnectionState.CONNECTING;
    case 'open':
      return WebSocketConnectionState.OPEN;
    case 'closing':
      return WebSocketConnectionState.CLOSING;
    case 'closed':
      return WebSocketConnectionState.CLOSED;
    default:
      return WebSocketConnectionState.NOT_AVAILABLE;
  }
}
